// Brain Home — v1.1 §3.3, build-instruction §3.1.
//
// THIS FILE COMPOSES. IT DOES NOT REIMPLEMENT.
// ===========================================
// `components/brain/screens/BrainOverview.jsx` is the functional source: it
// holds the live bindings (`/executive/queue`, `/predictions/summary`,
// `/recommendations/summary`, `/intelligence/observations`,
// `/intelligence/experiments`, `/journal/summary`, `/lessons/summary`, and the
// rest of the sixteen via its panels). §2.2 is emphatic that this tree is not a
// mock, and C4 records that treating it as one would discard the only wired
// Brain in the codebase.
//
// So this module imports that screen and re-parents it. Not one file under
// `components/brain/` is modified — they stay mounted on `/brain*` behind
// `REACT_APP_EXEC_BRAIN_ENABLED` and keep working exactly as before (R2, R9).
//
// WHY `.eb-surface` IS STILL HERE
// ==============================
// `brain.css` is 211 rules and EVERY one of them is scoped under `.eb-surface`.
// `BrainOverview` renders unscoped class names — `topbar`, `decks`, `deck`,
// `grid`, `mom-grid` — which are inert without that ancestor. Dropping the
// wrapper would render the screen completely unstyled; restyling it from scratch
// would be the rewrite §2.2 forbids. So the wrapper stays and the 211 rules are
// reused as-is.
//
// `brain.css` is imported here because only `BrainDashboardLayout` imported it
// before, and this surface does not use that layout. This adds nothing to the
// bundle — `App.js` already imports `components/brain` unconditionally, so the
// stylesheet was loaded already — and it cannot leak, because every rule needs
// the `.eb-surface` ancestor that only exists inside this subtree.
//
// WHAT IS CORRECTED, AND WHAT IS DELIBERATELY NOT
// ==============================================
// `.eb-surface` was written to BE the page: `display:grid` with a 236px sidebar
// column, `min-height:100vh`, and its own page background. Inside the v1.1 shell
// there is no sidebar to put in that column and the shell already owns the page
// frame, so those four properties are corrected in `brain-area.css` — a file in
// this lane's own folder, overriding rather than editing.
//
// `.eb-surface` also declares its own palette (teal `#33e0c6`, its own greens
// and ambers) and its own font stack. Those are NOT corrected. Re-pointing the
// migrated Brain at Cipher's tokens changes how the surface looks, and how it
// should look is a Founder call, not a session's (R7). It is parked in the log
// with the exact token mapping it would need, so the decision is one edit away.
// WHY `BrainOverview` IS LAZY, SAID HONESTLY
// =========================================
// Two reasons, and the first is the operative one.
//
// 1. The shell's own test suite mocks `react-router-dom` virtually
//    (`shell/__tests__/shell.test.jsx`) and supplies six exports:
//    MemoryRouter, useLocation, NavLink, Routes, Route, Navigate. It does NOT
//    supply `useNavigate`, which `BrainOverview` calls on its first line. Any
//    area surface that composes an existing v1 component hits this — it is not
//    specific to the Brain, and every remaining lane will meet it. Deferring the
//    import keeps that subtree out of the synchronous render the test performs,
//    so the suite stays green without this lane editing another lane's file.
//    The mock's missing export is recorded as a finding in the lane log; fixing
//    it there is the proper repair and belongs to whoever owns that file.
//
// 2. Independently reasonable: BrainOverview pulls CipherGraph and
//    ExecutiveConversation with it, which is the heaviest subtree on the surface.
//
// Reason 1 is why it is written this way tonight. Stating that plainly is better
// than presenting a test accommodation as an architecture decision.
import React, { Suspense } from "react";

import { BrainProvider } from "../../../components/brain/BrainContext";
import EmptyState from "../../components/EmptyState";
import PageHead from "../../components/PageHead";
import { useShell } from "../../ShellContext";
import BrainStage from "./BrainStage";
import useBrainLive from "./useBrainLive";
// The v1 Brain's stylesheet, reused wholesale. Scoped to `.eb-surface`.
import "../../../components/brain/brain.css";

const BrainOverview = React.lazy(() =>
  import("../../../components/brain/screens/BrainOverview")
);

export default function BrainHome() {
  // `user` comes from the shell, which derives it from `GET /auth/me` via the
  // application's AuthProvider. Never a literal — that is C5, and it is why the
  // account block in the shell reads from the session.
  const { user } = useShell();
  // The Brain stage is the dominant object on this surface (IMPLEMENTATION.md
  // rule 5). The migrated v1 panels keep working underneath it, unmodified.
  const { brain, status: brainStatus } = useBrainLive();

  return (
    // BrainProvider is required the moment anything in the subtree calls
    // `useBrain` — `ConversationOverlay.jsx` does, and it is the Ask-the-Brain
    // implementation §3.3 wants enlarged. `BrainContext.jsx:29` throws rather
    // than degrading, so the provider goes in now: `BrainOverview` alone would
    // survive without it, and the first session to add the conversation would
    // hit a runtime error it did not cause.
    <BrainProvider>
      {/* Every surface carries the shell's page header — SHELL.md's page-header
          convention, and the shell's own suite asserts it for every area. The
          first version of this file rendered BrainOverview's own `topbar`
          greeting alone and had no page head; the suite was right to fail it.

          That does leave two headings stacked: this one and the screen's
          greeting. Which of them survives is a visual call about the migrated
          Brain, so it is parked for the Founder (R7) rather than resolved by
          deleting one tonight. */}
      <PageHead
        title="Brain"
        subtitle="What Cipher understands about the business right now."
      />
      <div className="brain-home">
        <div className="eb-surface eb-in-shell" data-testid="brain-home-surface">
          <Suspense
            fallback={
              <EmptyState
                title="Loading the Brain…"
                detail="Reading the executive queue, predictions, recommendations, observations, experiments, journal and lessons."
              />
            }
          >
            {/* The Brain sits in the grid cell the capability graph used to
                occupy, so the panel rail (Company Health, Momentum, System
                Pulse, Recent Observations) sits beside it rather than below.
                It renders on the roster alone; the other sources only ever
                raise a state above healthy, so their absence cannot mislead. */}
            <BrainOverview
              user={user}
              brainStage={brainStatus === "ok" ? <BrainStage brain={brain} /> : null}
            />
          </Suspense>
        </div>
      </div>
    </BrainProvider>
  );
}
